import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { alpha } from '@mui/system';
import * as React from 'react';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import { theme } from 'src/theme';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { Theme, ButtonProps } from '@mui/material';

const colors: (keyof ( typeof theme.palette.colors))[] = [ 'green', 'red', 'grey', 'yellow', 'blue', 'greyLight', 'redLight', 'greenLight' ];

const useStyles = makeStyles(() => {
    return colors.reduce((aggr, color: keyof (typeof theme.palette.colors)) => {
        aggr[color] = {
            '&:hover': {
                // Reset on touch devices, it doesn't add specificity
                '@media (hover: none)': {
                    backgroundColor: alpha(theme.palette.colors[color].main || '', 0.7),
                    boxShadow: theme.shadows[1],
                },

                backgroundColor: alpha(theme.palette.colors[color].main || '', 0.7),

                boxShadow: theme.shadows[1],

                textDecoration: 'none',
            },
            backgroundColor: theme.palette.colors[color].main,
            borderColor: theme.palette.colors[color].dark,
            color: theme.palette.colors[color].contrastText,
            transition: theme.transitions.create(
                [ 'background-color', 'box-shadow', 'border-color', 'color' ],
                {
                    duration: theme.transitions.duration.short,
                },
            ),
        };
        return aggr;
    },
    {} as Record<keyof (typeof theme.palette.colors), any>);
});

type MyButtonProps = {
    color: (keyof ( typeof theme.palette.colors)) | null;
    radius: 'none' | 'large';
    size: 'small' | 'medium' | 'large';
    loading?: boolean;
    showLoading?: (() => void) | boolean;
};

const buttonStyle = makeStyles((theme: Theme) => ({
    root: ({ radius, size }: Partial<MyButtonProps>) => {
        const padding = size === 'small' ? theme.spacing(1, 2)
            : size === 'medium' ? theme.spacing(1.3, 4)
                : theme.spacing(1.5, 6);
        return {
            borderRadius: radius === 'large' ? theme.spacing(5) : theme.spacing(1),
            margin: theme.spacing(0, 1),
            maxWidth: '100%',
            padding,
        };
    },
}));

export const MyButton = ({ color = 'green', radius = 'none', size = 'small', loading = false, showLoading, sx = {}, ...props }: Omit<ButtonProps, keyof MyButtonProps> & Partial<MyButtonProps>) => {
    const styles = buttonStyle({ radius, size });
    const colors = useStyles();
    const [ localLoading, setLocalLoading ] = useState(loading);
    const buttonRef = useRef<HTMLButtonElement | null>(null);
    const [ width, setWidth ] = useState<number | string>('auto');
    //@ts-expect-error
    const onClick = useCallback(async (event: MouseEvent<HTMLButtonElement, MouseEvent>) => {
        if (showLoading) {
            setWidth(buttonRef?.current?.children[0]?.clientWidth || 'auto');
            if (typeof showLoading === 'function') showLoading();
            setLocalLoading(true);
        }
        if (props.onClick) props.onClick(event);
        return;
    }, [ props, showLoading ]);
    useEffect(() => {
        if (!loading) setLocalLoading(false);
    }, [ loading ]);
    //&nbsp; for same height

    props.children = loading && localLoading ? (
        <Box alignItems="center" display="flex" justifyContent="center" width={width}>
			&nbsp;<CircularProgress color="inherit" size={18} />&nbsp;
        </Box>
    ) : props.children;

    return (
        <Button
            disabled={loading && localLoading}
            ref={buttonRef}
            size={size}
            sx={sx}
            type="button"
            variant="contained"
            {...props}
            className={`${styles.root} ${color ? colors[color] : ''}`}
            onClick={onClick}
        />
    );
};

export const ButtonInGroup = ({ variant = 'contained', color = 'green', loading = false, showLoading, ...props }: Omit<ButtonProps, keyof MyButtonProps> & Partial<MyButtonProps>) => {
    const colors = useStyles();
    const [ localLoading, setLocalLoading ] = useState(loading);
    const [ width, setWidth ] = useState<number | string>('auto');
    const buttonRef = useRef<HTMLButtonElement | null>(null);
    //@ts-expect-error
    const onClick = useCallback(async (event: MouseEvent<HTMLButtonElement, MouseEvent>) => {
        if (showLoading) {
            setWidth(buttonRef?.current?.children[0]?.clientWidth || 'auto');
            if (typeof showLoading === 'function') showLoading();
            setLocalLoading(true);
        }
        if (props.onClick) props.onClick(event);
        return;
    }, [ props, showLoading ]);
    props.children = loading && localLoading ? (
        <Box alignItems="center" display="flex" justifyContent="center" width={width}>
		&nbsp;<CircularProgress color="inherit" size={18} />&nbsp;
        </Box>
    ) : props.children;
    return (
        <Button
            ref={buttonRef}
            variant={variant}
            {...props}
            className={clsx(color !== null ? [ props.className, colors[color] ] : [ props.className ])}
            onClick={onClick}
            type="button"
        />
    );
};
